import { Tooltip, TooltipTrigger } from '@trellis/roots/tooltip'
import type { ReminderRepeat, ReminderStepInput } from '@trellis/shared'
import { describeReminderMarkLabel, describeReminderOffset } from '@trellis/shared'

import { cn } from '@/lib/utils'

import { ReminderMarkDot, reminderMarkDotKind } from './ReminderMarkDot'
import {
  describeReminderOffsetSentence,
  ReminderMarkTooltipContent,
} from './ReminderMarkTooltipContent'
import { REMINDER_MARK_DOT_LABELS } from './reminders.constants'

/**
 * One small dot per step in day order, each with its day beneath. Hover a dot for what
 * the step does; the text for assistive tech is separate, so the hover target stays
 * hidden.
 */
export function ReminderStepIcons({
  steps,
  label,
  isDimmed = false,
  isWrapping = true,
}: {
  steps: readonly ReminderStepInput[]
  /** Accessible name of the list. */
  label: string
  isDimmed?: boolean
  /** Lets the dots flow onto a second line; turn off where the row must stay one line. */
  isWrapping?: boolean
}) {
  const sortedSteps = [...steps].sort(
    (left, right) => left.daysRelativeToDue - right.daysRelativeToDue,
  )
  return (
    <ul
      aria-label={label}
      className={cn(
        'flex gap-x-3 gap-y-2',
        isWrapping && 'flex-wrap',
        isDimmed && 'opacity-55',
      )}
    >
      {sortedSteps.map((step) => {
        const kind = reminderMarkDotKind({ ...step, isRepeat: false })
        return (
          <li key={step.daysRelativeToDue} className="min-w-7">
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className="flex cursor-default flex-col items-center gap-1"
                  aria-hidden="true"
                >
                  <ReminderMarkDot kind={kind} size="sm" />
                  <span className="text-muted-foreground text-[11px] tabular-nums">
                    {describeReminderMarkLabel(step.daysRelativeToDue)}
                  </span>
                </span>
              </TooltipTrigger>
              <ReminderMarkTooltipContent
                kind={kind}
                detail={describeReminderOffsetSentence(step.daysRelativeToDue)}
              />
            </Tooltip>
            <span className="sr-only">
              {REMINDER_MARK_DOT_LABELS[kind]}{' '}
              {describeReminderOffset(step.daysRelativeToDue)}
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/** Hollow repeat dot labelled "7d × 3", or a dash when the schedule doesn't repeat. */
export function ReminderRepeatIcon({
  repeat,
  isDimmed = false,
}: {
  repeat: ReminderRepeat | null
  isDimmed?: boolean
}) {
  if (!repeat) {
    return (
      <span className="text-muted-foreground">
        <span aria-hidden="true">—</span>
        <span className="sr-only">None</span>
      </span>
    )
  }
  return (
    <div className={cn('w-fit', isDimmed && 'opacity-55')}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className="flex cursor-default flex-col items-center gap-1"
            aria-hidden="true"
          >
            <ReminderMarkDot kind="repeat" size="sm" />
            <span className="text-muted-foreground text-[11px] whitespace-nowrap tabular-nums">
              {repeat.every}d × {repeat.max}
            </span>
          </span>
        </TooltipTrigger>
        <ReminderMarkTooltipContent
          kind="repeat"
          detail={`Every ${repeat.every} days, up to ${repeat.max} more times`}
        />
      </Tooltip>
      <span className="sr-only">
        Every {repeat.every} days, up to {repeat.max} more times
      </span>
    </div>
  )
}
